import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Lock, AlertCircle, Mail } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Checkbox } from '@/components/ui/checkbox';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/components/AuthProvider';
import { loginSchema, type LoginFormData } from '@/lib/validations';

interface LoginModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const LoginModal = ({ open, onOpenChange }: LoginModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signIn } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const handleLogin = async (data: LoginFormData) => {
    setIsLoading(true);
    console.log('LoginModal: Iniciando processo de login...');

    try {
      console.log('LoginModal: Chamando signIn...');
      await signIn(data.email, data.password);
      console.log('LoginModal: Login realizado com sucesso');
      
      toast({
        title: "Login realizado com sucesso!",
        description: "Redirecionando para o dashboard...",
      });
      
      // Fechar modal primeiro
      onOpenChange(false);
      
      // Aguardar o auth state atualizar e depois navegar
      setTimeout(() => {
        console.log('LoginModal: Navegando para dashboard...');
        window.location.href = '/dashboard';
      }, 500);
      
    } catch (error: any) {
      console.error('LoginModal: Erro no login:', error);
      setIsLoading(false);
      toast({
        title: "Erro no login",
        description: error.message || 'Erro no login. Verifique suas credenciais.',
        variant: "destructive",
      });
    }
  };

  const handleForgotPassword = async (email: string) => {
    if (!email) {
      toast({
        title: "E-mail necessário",
        description: "Por favor, insira seu e-mail no campo acima.",
        variant: "destructive",
      });
      return;
    }

    setIsResetting(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/`,
      });

      if (error) throw error;

      toast({
        title: "E-mail enviado!",
        description: "Verifique seu e-mail para redefinir sua senha.",
      });
    } catch (error: any) {
      console.error('Erro ao enviar e-mail de recuperação:', error);
      toast({
        title: "Erro ao enviar e-mail",
        description: error.message || 'Não foi possível enviar o e-mail de recuperação.',
        variant: "destructive",
      });
    } finally {
      setIsResetting(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) throw error;
    } catch (error: any) {
      console.error('Erro no login com Google:', error);
      setIsLoading(false);
      toast({
        title: "Erro no login com Google",
        description: error.message || 'Não foi possível fazer login com Google.',
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl md:text-2xl font-bold text-center text-primary">
            Entrar no Obrim
          </DialogTitle>
          <DialogDescription className="text-center text-sm">
            Digite suas credenciais para acessar
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(handleLogin)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                {...register('email')}
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                enterKeyHint="next"
                placeholder="seu@email.com"
                className="pl-10 min-h-[44px]"
              />
            </div>
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password">Senha</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                {...register('password')}
                id="password"
                type="password"
                autoComplete="current-password"
                enterKeyHint="go"
                placeholder="Digite sua senha"
                className="pl-10 min-h-[44px]"
              />
            </div>
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="remember" 
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked as boolean)}
              />
              <label
                htmlFor="remember"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Permanecer conectado
              </label>
            </div>
            <Button
              type="button"
              variant="link"
              className="text-xs p-0 h-auto"
              disabled={isResetting}
              onClick={() => {
                const emailInput = document.getElementById('email') as HTMLInputElement;
                handleForgotPassword(emailInput?.value || '');
              }}
            >
              {isResetting ? 'Enviando...' : 'Esqueci minha senha'}
            </Button>
          </div>

          <Button type="submit" className="w-full min-h-[48px] text-base" disabled={isLoading}>
            {isLoading ? 'Entrando...' : 'Entrar'}
          </Button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Ou continue com</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full min-h-[48px] text-base"
            onClick={handleGoogleLogin}
            disabled={isLoading}
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Continuar com Google
          </Button>
        </form>

        <div className="text-center space-y-4">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Não tem conta?</span>
            </div>
          </div>
          
          <Button 
            type="button" 
            variant="outline" 
            className="w-full min-h-[48px] text-base"
            onClick={() => {
              onOpenChange(false);
              navigate('/register');
            }}
          >
            Criar nova conta
          </Button>
        </div>

        <div className="text-center text-sm text-muted-foreground">
          <p>Plataforma de gestão para engenheiros e arquitetos</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};